import React from 'react';
import { graphql, PageProps } from 'gatsby';
import Layout, { LayoutTheme } from 'src/elements/layout/layout';
import getDynamicContentfulComponent from 'src/functions/dynamicComponentFactory';
import { DynamicSectionColumnContainer, DynamicSectionContainer } from './dynamicPage.styles';
import { SectionDynamicContentfulModel } from 'src/api/models/SectionDynamic';
import { SectionColumnDynamicContentfulModel } from 'src/api/models/SectionColumnDynamic';

export interface DynamicPageData {
  contentfulPageDynamic: {
    title: string;
    slug: string;
    canonicalTagSlug?: string;
    theme: string;
    seoTitle?: string;
    seoDescription?: string;
    seoImage?: {
      file?: {
        url: string;
      }
    }
    contentfulchildren: (SectionDynamicContentfulModel | SectionColumnDynamicContentfulModel)[];
  }
}

interface DynamicPageTemplateProps extends PageProps {
  data: DynamicPageData;
}

const TOOLBAR_THEMES: { [key: string]: LayoutTheme } = {
  Standard: 'default',
  Pro: 'pro'
}

export const DynamicPageRenderer = (props: DynamicPageTemplateProps): JSX.Element => {
  const dynamicPageData = props.data.contentfulPageDynamic;

  return (
    <Layout
      helmet={{
        title: dynamicPageData.seoTitle,
        description: dynamicPageData.seoDescription,
        previewImageURL: dynamicPageData.seoImage?.file?.url
      }}
      location={props.location}
      theme={TOOLBAR_THEMES[ dynamicPageData.theme ] ?? 'default'}
      canonicalTagSlug={dynamicPageData.canonicalTagSlug ? `/${dynamicPageData.canonicalTagSlug}` : null}
    >
      {
        dynamicPageData.contentfulchildren.map((section, index) => {
          if (section.internal.type === 'ContentfulSectionDynamic') {
            const sectionData = section as SectionDynamicContentfulModel;

            return (
              <DynamicSectionContainer
                key={index}
                background={sectionData.backgroundColor}
                horizontalPadding={sectionData.horizontalPadding}
                verticalPadding={sectionData.verticalPadding}
                maxWidth={sectionData.maximumWidth}
                marginTop={sectionData.topMargin}
              >
                {getDynamicContentfulComponent(sectionData)}
              </DynamicSectionContainer>
            )
          } else {
            const { columnWidthDistribution, contentfulchildren, maxWidth, responsiveBreakpoint }  = section as SectionColumnDynamicContentfulModel;
            const fistColumnPercentage = columnWidthDistribution ?? 50;

            return (
              <DynamicSectionColumnContainer
                firstGridColumnSize={fistColumnPercentage}
                secondGridColumnSize={100 - fistColumnPercentage}
                maxWidth={maxWidth}
                breakpoint={responsiveBreakpoint}
              >
                {
                  contentfulchildren.map((column, index) => (
                    <DynamicSectionContainer
                      key={index}
                      background={column.backgroundColor}
                      horizontalPadding={column.horizontalPadding}
                      verticalPadding={column.verticalPadding}
                      maxWidth={column.maximumWidth}
                      isColumn
                    >
                      {getDynamicContentfulComponent(column)}
                    </DynamicSectionContainer>
                  ))
                }
              </DynamicSectionColumnContainer>
            )
          }
        })
      }
    </Layout>
  )
}

const DynamicPageTemplate = (props: DynamicPageTemplateProps): JSX.Element => (
  <DynamicPageRenderer {...props}/>
)

export default DynamicPageTemplate

export const pageQuery = graphql`
  query DynamicPageBySlug($slug: String!) {
    contentfulPageDynamic(slug: { eq: $slug }) {
      seoTitle
      seoDescription
      theme
      canonicalTagSlug
      seoImage {
        file {
          url
        }
      }
      title
      contentfulchildren {
        ...SectionDynamicContent
        ...SectionColumnDynamicContent
      }
    }
  }
`
